import React, { useCallback, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import Toast from "./Toast";
import type { VisibilitySetting } from "../types/UserTypes";
import styles from "./Visibility.module.css";

/** Map page prefixes to display labels and the feature keys that belong to each page */
const PAGE_GROUPS: { prefix: string; label: string }[] = [
  { prefix: "timeentry_show_", label: "Time Entry" },
  { prefix: "approvals_show_", label: "Approvals" },
  { prefix: "reports_show_", label: "Reports" },
];

const Visibility = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const { visibility, loading, refreshTier } = useTier();

  const [toggling, setToggling] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const handleToggle = useCallback(
    async (setting: VisibilitySetting) => {
      if (!api || toggling) return;
      setToggling(setting.key);
      try {
        await api.put(`visibility/${setting.key}`, {
          visible: !setting.visible,
        });
        await refreshTier();
        setToast({ message: `${setting.displayName} ${setting.visible ? "hidden" : "shown"}.`, type: "success" });
      } catch (err: any) {
        console.error("Toggle failed:", err);
        const msg = err?.details?.message || err?.message || "Failed to update setting.";
        setToast({ message: msg, type: "error" });
      } finally {
        setToggling(null);
      }
    },
    [api, toggling, refreshTier],
  );

  // Group visibility settings by page
  const grouped = useMemo(() => {
    return PAGE_GROUPS.map((pg) => ({
      label: pg.label,
      items: visibility.filter((v) => v.key.startsWith(pg.prefix)),
    })).filter((g) => g.items.length > 0);
  }, [visibility]);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <div className={styles["vis-shell"]}>
        {loading ? (
          <div className={styles["vis-card"]}>
            <div className={styles["vis-card-header"]}>
              <span className={styles["vis-card-header-title"]}>Loading…</span>
            </div>
            <div>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles["vis-row"]} style={{ opacity: 0.5 }}>
                  <div className={styles["vis-left"]}>
                    <span className={styles["vis-skeleton"]} style={{ width: 20, height: 20, borderRadius: 6 }} />
                    <div>
                      <span className={styles["vis-skeleton"]} style={{ width: 140, height: 13 }} />
                      <span className={styles["vis-skeleton"]} style={{ width: 220, height: 11, marginTop: 4 }} />
                    </div>
                  </div>
                  <div className={styles["vis-right"]}>
                    <span className={styles["vis-skeleton"]} style={{ width: 36, height: 20, borderRadius: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : grouped.length === 0 ? (
          <div className={styles["vis-empty-state"]}>
            <div className={styles["vis-empty-icon"]}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="#9ca3af" strokeWidth="1.5" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="#9ca3af" strokeWidth="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="#9ca3af" strokeWidth="1.5" />
                <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="#9ca3af" strokeWidth="1.5" />
              </svg>
            </div>
            <h3 className={styles["vis-empty-title"]}>No visibility settings yet</h3>
            <p className={styles["vis-empty-subtitle"]}>
              Visibility settings let you control which fields your team sees on each page.
              They'll appear here once configured for your plan.
            </p>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.label} className={styles["vis-card"]}>
              <div className={styles["vis-card-header"]}>
                <span className={styles["vis-card-header-title"]}>{group.label}</span>
              </div>
              <div>
                {group.items.map((setting) => {
                  const isHidden = !setting.visible;
                  const rowClass = [
                    styles["vis-row"],
                    isHidden ? styles["vis-row-disabled"] : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <div className={rowClass} key={setting.key} data-testid={`vis-row-${setting.key}`}>
                      <div className={styles["vis-left"]}>
                        <span className={styles["vis-icon"]}>
                          {setting.visible ? (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <rect width="20" height="20" rx="6" fill="#dcfce7" />
                              <path d="M6 10l3 3 5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <rect x="1" y="1" width="18" height="18" rx="5" stroke="#d1d5db" strokeWidth="1.5" />
                            </svg>
                          )}
                        </span>
                        <div>
                          <div className={styles["vis-name"]}>{setting.displayName}</div>
                          {setting.description && (
                            <div className={styles["vis-desc"]}>{setting.description}</div>
                          )}
                        </div>
                      </div>
                      <div className={styles["vis-right"]}>
                        {isHidden && (
                          <span className={styles["vis-disabled-note"]}>Hidden</span>
                        )}
                        <button
                          type="button"
                          className={`${styles.toggle} ${setting.visible ? styles["toggle-on"] : styles["toggle-off"]}`}
                          onClick={() => handleToggle(setting)}
                          disabled={toggling === setting.key}
                          aria-label={`Toggle ${setting.displayName}`}
                        >
                          <span className={styles["toggle-knob"]} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </MsalAuthenticationTemplate>
  );
};

export default Visibility;
